'use client';

import { useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import FadeIn from '../ui/FadeIn';
import { useTranslations } from '@/lib/i18n/TranslationProvider';
import { Play } from 'lucide-react';

interface VideoShowcaseProps {
  onVisibilityChange: (isVisible: boolean) => void;
  onExpand: () => void;
}

export default function VideoShowcase({ onVisibilityChange, onExpand }: VideoShowcaseProps) {
  const { t } = useTranslations();
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { amount: 0.3 });

  // Notify parent of visibility changes to trigger PiP
  useEffect(() => {
    onVisibilityChange(isInView);
  }, [isInView, onVisibilityChange]);

  return (
    <section 
      ref={containerRef}
      id="demo" 
      className="py-24 bg-[var(--color-md-surface)] relative overflow-hidden"
    >
      {/* Ambient background accent */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-[var(--color-md-primary)]/[0.03] rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <FadeIn>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--color-md-surface-variant)]/50 border border-[var(--color-md-outline-variant)] text-[10px] uppercase font-bold tracking-[0.15em] text-[var(--color-md-primary)] mb-6">
              {t.videoShowcase.badge}
            </div>
            <h2 className="text-4xl md:text-6xl font-black text-[var(--color-md-on-surface)] tracking-tight mb-6 leading-tight">
              {t.videoShowcase.title} <span className="text-brand-gradient">{t.videoShowcase.titleAccent}</span>
            </h2>
            <p className="text-lg md:text-xl text-[var(--color-md-on-surface-variant)] font-medium max-w-2xl mx-auto leading-relaxed">
              {t.videoShowcase.subtitle}
            </p>
          </FadeIn>
        </div>

        {/* Video Frame */}
        <FadeIn delay={0.2} yOffset={40}>
          <div 
            className="relative group max-w-5xl mx-auto rounded-[32px] p-2 md:p-4 frosted-glass shadow-cinematic-4 cursor-pointer"
            onClick={onExpand}
          >
            <div className="relative rounded-[24px] overflow-hidden bg-black border border-white/5 aspect-video shadow-2xl">
              <video
                src="/demo.mp4"
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover"
              />
              
              {/* Play Overlay on Hover */}
              <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30 scale-90 group-hover:scale-100 transition-transform">
                  <Play size={32} fill="white" className="text-white ml-1" />
                </div>
              </div>

              {/* Bottom Label */}
              <div className="absolute bottom-6 left-6 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-md border border-white/10 text-[10px] font-black text-white/90 uppercase tracking-widest">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                Live Product Walkthrough
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}
